import { CpuChipIcon, TagIcon } from "@heroicons/react/24/outline";
import { Container, rem, Stack, Tabs, Text, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import {
  DictionaryItem,
  DictionaryManagement,
  DictionaryService,
} from "../../components/feature-specific/DictionaryManagement";
import { TechnologyCategoriesManagement } from "../../components/feature-specific/TechnologyCategoriesManagement";
import { useTechnologiesDictionary } from "../../hooks/queries/useTechnologiesDictionary";
import { ConsolidateTechnologiesRequest, TechnologyDictionaryModel } from "../../models/technology.model";
import { technologyDictionaryAdminService } from "../../services/admin";

// Adapter type for technologies to work with DictionaryItem interface
interface TechnologyDictionaryAdapter extends DictionaryItem {
  id: number;
  title: string;
  name: string;
}

export function OperationsTechnologiesManagement() {
  const [activeTab, setActiveTab] = useState<string | null>("technologies");

  // Use singleton instance
  const technologyAdminService = technologyDictionaryAdminService;
  const queryClient = useQueryClient();
  const { data: technologies, isLoading: technologiesLoading } =
    useTechnologiesDictionary();

  // Adapt TechnologyDictionaryAdminService to DictionaryService interface
  const technologyDictionaryService: DictionaryService<TechnologyDictionaryAdapter> =
    useMemo(
      () => ({
        getDictionary: async () => {
          // Read from React Query cache to get the most up-to-date data
          const techs =
            queryClient.getQueryData<TechnologyDictionaryModel[]>([
              "technologies",
              "dictionary",
            ]) || [];
          return techs.map((tech) => ({
            ...tech,
            title: tech.name,
            name: tech.name,
          }));
        },
        createItem: async (item: Omit<TechnologyDictionaryAdapter, "id">) => {
          // TODO: Backend implementation missing - this will throw an error until endpoints are created
          const result = await technologyAdminService.createTechnology({
            name: item.name || item.title || "",
          });
          return {
            ...result,
            title: result.name,
            name: result.name,
          } as TechnologyDictionaryAdapter;
        },
        updateItem: async (
          id: number,
          item: Partial<TechnologyDictionaryAdapter>
        ) => {
          // TODO: Backend implementation missing - this will throw an error until endpoints are created
          const result = await technologyAdminService.updateTechnology(id, {
            id,
            name: item.name || item.title || "",
          });
          return {
            ...result,
            title: result.name,
            name: result.name,
          } as TechnologyDictionaryAdapter;
        },
        deleteItem: async (id: number) => {
          // TODO: Backend implementation missing - this will throw an error until endpoints are created
          await technologyAdminService.deleteTechnology(id);
        },
        consolidateItems: async (sourceId: number, targetId: number) => {
          const result = await technologyAdminService.consolidateTechnologies({
            sourceTechnologyId: sourceId,
            targetTechnologyId: targetId,
          } as ConsolidateTechnologiesRequest);

          // Force-refetch the dictionary so getDictionary() returns fresh data
          await queryClient.refetchQueries({
            queryKey: ["technologies", "dictionary"],
          });

          return {
            ...result,
            title: result.name,
            name: result.name,
          } as TechnologyDictionaryAdapter;
        },
      }),
      [queryClient]
    );

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Breadcrumb
            items={[
              { label: "Operations Panel", href: "/operations" },
              { label: "Dictionaries", href: "/operations/dictionaries" },
              { label: "Technologies Management", isActive: true },
            ]}
          />
          <Title order={1} size="h2" fw={700}>
            Technologies Management
          </Title>
          <Text c="dimmed" size="lg">
            Manage technologies, programming languages, and their categories
          </Text>
        </div>

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List mb="md">
            <Tabs.Tab
              value="technologies"
              leftSection={<CpuChipIcon style={{ width: rem(16) }} />}
            >
              Skills & Technologies
            </Tabs.Tab>
            <Tabs.Tab
              value="categories"
              leftSection={<TagIcon style={{ width: rem(16) }} />}
            >
              Categories & Mappings
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="technologies">
            <DictionaryManagement
              title="Skills & Technologies"
              service={technologyDictionaryService}
              itemDisplayName="Skill or Technology"
              itemDisplayNamePlural="Skills & Technologies"
              canConsolidate={true}
              titleFieldName="title"
              searchPlaceholder="Search technologies..."
            />
          </Tabs.Panel>

          <Tabs.Panel value="categories">
            <TechnologyCategoriesManagement />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
